import { Button, DialogActions, DialogTitle, Icon } from "@mui/material";
import React, { Component } from "react";
import styled from "styled-components";

import { PREDEFINED_PALETTES } from "common/constants";
import { ActionPermissionsObject } from "common/interfaces";
import AppController from "js/core/AppController";
import { ds } from "js/core/models/dataService";
import { Theme } from "js/core/models/theme";
import { app } from "js/namespaces";
import { _ } from "js/vendor";

import { BeautifulDialog, DialogContent, ShowConfirmationDialog } from "js/react/components/Dialogs/BaseDialog";
import { FlexSpacer, Gap10, Gap20 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";

import WorkspaceController from "js/controllers/WorkspaceController";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { ThemeThumbnailList } from "js/editor/ThemeEditor/components/ThemeThumbnailList";
import { BlueButton } from "js/react/components/UiComponents";

import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import "css/style-guide.scss";
import { getPresentation } from "../../core/models/presentation";
import { dialogTheme } from "../../react/materialThemeOverrides";
import { withTranslation } from "react-i18next";

const Subtitle = styled.div`
    margin-top: 10px;

    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 125%;
    letter-spacing: 0.5px;
    color: #222222;
`;

class ChooseThemeDialogBase extends Component {
    static defaultProps = {
        title: "Select Theme",
        subtitle: null,
        selectButtonLabel: "Select Theme",
        preventClose: false,
        hideUserThemes: false,
        hideBuiltInThemes: false,
        onlyTeamThemes: false,
        showCancelButton: true,
        closeDialogOnThemeChoose: true,
        callback: null,
        selectedThemeId: null,
    }

    state = {
        selectedTheme: null,
        teamThemes: [],
        userThemes: [],
        builtInThemes: [],
        pptTheme: null
    }

    componentDidMount() {
        this.loadThemes().then(() => {
            const { hideBuiltInThemes, hideUserThemes, selectedThemeId } = this.props;
            const { teamThemes, userThemes, builtInThemes, pptTheme } = this.state;
            // Auto select ppt theme if there are no other options
            if (
                pptTheme &&
                hideBuiltInThemes &&
                !teamThemes.length &&
                (
                    !userThemes.length ||
                    hideUserThemes
                )
            ) {
                this.handleChooseTheme(pptTheme);
            }

            // Set the selected theme if a selectedThemeId is provided
            if (selectedThemeId) {
                const selectedTheme = teamThemes.find(theme => theme.id === selectedThemeId) ||
                    (!hideUserThemes && userThemes.find(theme => theme.id === selectedThemeId)) ||
                    (!hideBuiltInThemes && builtInThemes.find(theme => theme.id === selectedThemeId));

                if (selectedTheme) {
                    this.setState({ selectedTheme });
                }
            }
        });
    }

    setStateAsync(stateUpdates) {
        return new Promise(resolve => this.setState(stateUpdates, resolve));
    }

    async loadThemes() {
        const { pptThemeModel } = this.props;

        const teamThemes = await WorkspaceController.getSharedThemes(true, true);
        const userThemes = await WorkspaceController.getUserThemes(true);
        const builtInThemes = ds.builtInThemes.models;

        const pptTheme = pptThemeModel && new Theme(
            pptThemeModel,
            {
                disconnected: true,
                autoSync: false
            }
        );

        return new Promise(resolve => this.setState(
            {
                teamThemes,
                userThemes,
                builtInThemes,
                pptTheme,
            },
            resolve
        ));
    }

    handleSelectTheme = selectedTheme => {
        this.setState({ selectedTheme });
    }

    handleChooseTheme = async theme => {
        this.handleSelectTheme();

        const { callback, closeDialog, closeDialogOnThemeChoose } = this.props;

        if (!theme.has("colors")) {
            const defaultThemePalette = _.find(PREDEFINED_PALETTES, { id: theme.get("palette_name") || "colorful" });
            theme.set("colors", defaultThemePalette.colors);
        }

        if (callback) {
            await callback(theme);
        }

        if (closeDialogOnThemeChoose) {
            closeDialog(theme);
        }
    }

    handleEditCurrentTheme = async () => {
        const { closeDialog } = this.props;

        closeDialog();

        await PresentationEditorController.showThemeEditor();
    }

    handleDeleteTheme = theme => {
        const { t } = this.props;
        ShowConfirmationDialog({
            title: t("Are you sure you want to delete this theme?"),
            message: t("No existing presentations will be affected by this change but you won't be able to use this theme in any future presentations.")
        }).then(confirmed => {
            if (confirmed) {
                theme.collection.remove([theme]);
                theme.destroy();
            }
        });
    }

    render() {
        const { t } = this.props;
        const {
            selectedTheme,
            pptTheme
        } = this.state;
        const {
            closeDialog,
            title,
            subtitle,
            selectButtonLabel,
            preventClose,
            onlyTeamThemes,
            showCancelButton,
            styles,
            showEditCurrentTheme = true,
            fullScreen = false
        } = this.props;

        // If we're creating a theme, we won't have a presentation
        const isTeamTheme = PresentationEditorController.presentation?.has("sharedThemeId");

        return (
            <BeautifulDialog
                maxWidth="lg"
                fullScreen={app.isMobileOrTablet || fullScreen}
                preventClose={preventClose}
                closeDialog={closeDialog}
            >
                <DialogTitle>
                    <FlexBox left>{t(title)}
                        <FlexSpacer />
                        {showEditCurrentTheme && (!isTeamTheme || WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage) &&
                            <Button onClick={this.handleEditCurrentTheme}>
                                <Icon color="primary">edit</Icon>
                                {t("Edit Current Theme...")}
                            </Button>
                        }
                    </FlexBox>
                    {subtitle && <Subtitle>{t(subtitle)}</Subtitle>}
                </DialogTitle>
                <DialogContent style={{ ...styles, background: "#f1f1f1", overflow: app.isMobileOrTablet ? "hidden" : null }}>
                    <Gap10 />
                    <ThemeThumbnailList
                        t={t}
                        requireTeamTheme={onlyTeamThemes}
                        pptTheme={pptTheme}
                        selectedTheme={selectedTheme}
                        onClick={this.handleSelectTheme}
                        onDoubleClick={this.handleChooseTheme}
                    />
                </DialogContent>
                <DialogActions>
                    <FlexBox fillWidth>
                        <FlexSpacer />
                        {showCancelButton && <Button onClick={closeDialog}>Cancel</Button>}
                        <Gap20 />
                        <BlueButton
                            color="primary"
                            onClick={() => this.handleChooseTheme(selectedTheme)}
                            disabled={!selectedTheme}
                        >
                            {t(selectButtonLabel)}
                        </BlueButton>
                    </FlexBox>
                </DialogActions>
            </BeautifulDialog>
        );
    }
}

export class SwitchThemeRouteWrapper extends React.Component {
    handleSelectTheme = async selectedTheme => {
        const { presentationId } = this.props;
        const presentation = await getPresentation(presentationId, { permission: "write", autoSync: false });
        await app.themeManager.saveThemeToPresentation(selectedTheme, presentation);
    }

    handleCancel = () => {
        const { returnPath, presentationId } = this.props;
        // Go to the presentation with the currect slide index
        if (returnPath) AppController.goToRoute(returnPath);
        // keeping it just if a user removes the returnPath from the URL and then refreshes the page - it will still work
        // but will go to the first slide
        else AppController.showEditor({ presentationId });
    }

    render() {
        return (
            <MuiThemeProvider theme={dialogTheme}>
                <ChooseThemeDialog showEditCurrentTheme={false} fullScreen callback={this.handleSelectTheme} closeDialog={this.handleCancel} />
            </MuiThemeProvider>
        );
    }
}
export const ChooseThemeDialog = withTranslation()(React.forwardRef((props, ref) => (
    <ChooseThemeDialogBase {...props} ref={ref} />
)));
